import React, { useState, useCallback } from 'react'
import { Marker } from 'react-map-gl/mapbox'
import { Box, Typography, LinearProgress, useTheme } from '@mui/material'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import FactoryIcon from '@mui/icons-material/Factory'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

const nodeConfig = {
  supplier: {
    Icon: WarehouseIcon,
    label: 'Supplier',
    color: '#10b981',
    darkColor: '#059669',
    glow: 'rgba(16, 185, 129, 0.35)',
  },
  factory: {
    Icon: FactoryIcon,
    label: 'Factory',
    color: '#f59e0b',
    darkColor: '#d97706',
    glow: 'rgba(245, 158, 11, 0.35)',
  },
  distributor: {
    Icon: LocalShippingIcon,
    label: 'Distributor',
    color: '#3b82f6',
    darkColor: '#2563eb',
    glow: 'rgba(59, 130, 246, 0.35)',
  },
  retailer: {
    Icon: StorefrontIcon,
    label: 'Retailer',
    color: '#8b5cf6',
    darkColor: '#7c3aed',
    glow: 'rgba(139, 92, 246, 0.35)',
  },
}

export { nodeConfig }

// Helper: get status color based on fill rate
function getStatusColor(fillRate, isDark) {
  if (fillRate === null) return isDark ? '#94a3b8' : '#6b7280'
  if (fillRate >= 90) return isDark ? '#34d399' : '#059669'
  if (fillRate >= 70) return isDark ? '#fbbf24' : '#d97706'
  return isDark ? '#f87171' : '#dc2626'
}

function MapMarker({ node, selected, onClick, onDragEnd, themeMode, connectSource, onStartConnect, nodeKpis, onViewNodeResults, showLabel = true }) {
  const [hovered, setHovered] = useState(false)
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const nodeType = node.data.nodeType || 'supplier'
  const config = nodeConfig[nodeType] || nodeConfig.supplier
  const { Icon } = config
  const bgColor = isDark ? config.darkColor : config.color

  const isConnectSource = connectSource === node.id
  const isConnectTarget = connectSource && connectSource !== node.id

  const statusColor = nodeKpis ? getStatusColor(nodeKpis.fillRate, isDark) : null

  const handleDragEnd = useCallback(
    evt => {
      onDragEnd(node.id, evt.lngLat.lat, evt.lngLat.lng)
    },
    [node.id, onDragEnd]
  )

  const handleClick = useCallback(
    e => {
      e.originalEvent?.stopPropagation()
      onClick(node)
    },
    [node, onClick]
  )

  const handleHandleMouseDown = useCallback(
    e => {
      e.stopPropagation()
      e.preventDefault()
      onStartConnect(node.id)
    },
    [node.id, onStartConnect]
  )

  const handleViewDetails = useCallback(
    e => {
      e.stopPropagation()
      e.preventDefault()
      if (onViewNodeResults) {
        onViewNodeResults(node.id)
      }
    },
    [node.id, onViewNodeResults]
  )

  return (
    <Marker
      longitude={node.data.lng}
      latitude={node.data.lat}
      anchor="center"
      draggable={!connectSource}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: isConnectTarget ? 'pointer' : connectSource ? 'default' : 'grab',
        }}
      >
        {/* Pulsing ring for connect source */}
        {isConnectSource && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 64,
              height: 64,
              borderRadius: '18px',
              border: `2px solid ${bgColor}`,
              opacity: 0.6,
              animation: 'pulse-ring 1.5s ease-in-out infinite',
              '@keyframes pulse-ring': {
                '0%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.6 },
                '50%': { transform: 'translate(-50%, -50%) scale(1.2)', opacity: 0.2 },
                '100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.6 },
              },
            }}
          />
        )}

        {/* Target highlight glow when hovering during connect mode */}
        {isConnectTarget && hovered && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 60,
              height: 60,
              borderRadius: '17px',
              border: `2px solid ${isDark ? '#818cf8' : '#4f46e5'}`,
              bgcolor: isDark ? 'rgba(129,140,248,0.1)' : 'rgba(79,70,229,0.08)',
            }}
          />
        )}

        {/* Node body */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '14px',
            backgroundColor: bgColor,
            transition: 'all 0.2s ease',
            transform: hovered ? 'scale(1.12)' : 'scale(1)',
            boxShadow: hovered
              ? `0 6px 20px ${config.glow}, 0 0 0 3px ${bgColor}33`
              : `0 3px 12px ${config.glow}`,
            outline: selected ? `2.5px solid ${isDark ? '#fff' : '#1e293b'}` : '2.5px solid transparent',
            outlineOffset: 3,
          }}
        >
          <Icon
            sx={{
              fontSize: 26,
              color: '#fff',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))',
            }}
          />
        </Box>

        {/* Status dot — top-right corner, always visible post-simulation */}
        {nodeKpis && nodeKpis.fillRate !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: statusColor,
              border: `2px solid ${isDark ? '#1e293b' : '#fff'}`,
              boxShadow: `0 1px 4px rgba(0,0,0,0.3)`,
              zIndex: 4,
            }}
          />
        )}

        {/* Connection handle — right side, visible on hover, hidden during active connect */}
        {hovered && !connectSource && (
          <Box
            onMouseDown={handleHandleMouseDown}
            sx={{
              position: 'absolute',
              right: -10,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: isDark ? '#818cf8' : '#4f46e5',
              border: `2.5px solid ${isDark ? '#1e293b' : '#fff'}`,
              cursor: 'crosshair',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px rgba(0,0,0,0.25), 0 0 0 2px ${isDark ? 'rgba(129,140,248,0.3)' : 'rgba(79,70,229,0.2)'}`,
              transition: 'all 0.15s ease',
              zIndex: 5,
              '&:hover': {
                transform: 'translateY(-50%) scale(1.3)',
                bgcolor: isDark ? '#6366f1' : '#4338ca',
              },
              '&::after': {
                content: '""',
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#fff',
              },
            }}
          />
        )}

        {/* Label below marker */}
        {showLabel && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: '11px',
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#1e293b',
              whiteSpace: 'nowrap',
              textShadow: isDark
                ? '0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.5)'
                : '0 1px 3px rgba(255,255,255,0.9), 0 0 6px rgba(255,255,255,0.5)',
              letterSpacing: '0.01em',
              lineHeight: 1.2,
            }}
          >
            {node.data.label}
          </Typography>
        )}

        {/* Simple name tooltip — shows on hover only when labels are hidden (pre-simulation) */}
        {hovered && !nodeKpis && !showLabel && (
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              left: 'calc(100% + 8px)',
              zIndex: 10,
              pointerEvents: 'none',
              bgcolor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(12px)',
              borderRadius: '8px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.45)' : '0 4px 16px rgba(0,0,0,0.12)',
              px: 1.5,
              py: 0.8,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.2 }}>
              {nodeConfig[nodeType]?.label || 'Node'}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
              {node.data.label}
            </Typography>
          </Box>
        )}

        {/* KPI tooltip — appears on hover post-simulation (always shown, name hidden if label is visible) */}
        {nodeKpis && hovered && (
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              left: 'calc(100% + 8px)',
              zIndex: 10,
              minWidth: 160,
              bgcolor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(16px)',
              borderRadius: '10px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.15)',
              px: 1.5,
              py: 1.2,
              pointerEvents: 'auto',
            }}
          >
            {/* Header */}
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: isDark ? '#94a3b8' : '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {nodeConfig[nodeType]?.label || 'Node'}
            </Typography>
            {!showLabel && (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  mb: 0.8,
                }}
              >
                {node.data.label}
              </Typography>
            )}

            {/* Fill Rate */}
            {nodeKpis.fillRate !== null && (
              <Box sx={{ mb: 0.8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>
                    Fill Rate
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: statusColor,
                    }}
                  >
                    {nodeKpis.fillRate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(nodeKpis.fillRate, 100)}
                  sx={{
                    height: 3,
                    borderRadius: 2,
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                      bgcolor: statusColor,
                    },
                  }}
                />
              </Box>
            )}

            {/* Profit */}
            {nodeKpis.profit !== null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>
                  Profit
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: nodeKpis.profit >= 0
                      ? (isDark ? '#34d399' : '#059669')
                      : (isDark ? '#f87171' : '#dc2626'),
                  }}
                >
                  {Math.abs(nodeKpis.profit) >= 1000
                    ? `${nodeKpis.profit >= 0 ? '+' : '-'}$${(Math.abs(nodeKpis.profit) / 1000).toFixed(1)}k`
                    : `${nodeKpis.profit >= 0 ? '+' : ''}$${nodeKpis.profit.toFixed(0)}`}
                </Typography>
              </Box>
            )}

            {/* Inventory */}
            {nodeKpis.inventoryLevel != null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280' }}>
                  Inventory
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  }}
                >
                  {nodeKpis.inventoryLevel >= 1000
                    ? `${(nodeKpis.inventoryLevel / 1000).toFixed(1)}k`
                    : nodeKpis.inventoryLevel} units
                </Typography>
              </Box>
            )}

            {/* View Details link */}
            {onViewNodeResults && (
              <Box
                onClick={handleViewDetails}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  pt: 0.5,
                  borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  '&:hover': {
                    '& .view-details-text': {
                      color: isDark ? '#a5b4fc' : '#4338ca',
                    },
                  },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 11, color: isDark ? '#818cf8' : '#4f46e5' }} />
                <Typography
                  className="view-details-text"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: isDark ? '#818cf8' : '#4f46e5',
                    transition: 'color 0.15s ease',
                  }}
                >
                  View Details
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Marker>
  )
}

export default React.memo(MapMarker)
